import {
  DeleteOutline as DeleteOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import { Accordion, Box, Chip, IconButton } from '@mui/material';
import { ReactElement } from 'react';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { getPlainText } from 'src/utils';

import { AccordionProps } from './SidebarAccordion.interface';
import {
  AccordionContainer,
  LabelsContainer,
  NumberContainer,
  SortableItemContainer,
  StepContainer,
  StyledAccordian,
  Summary,
  SummaryLabel,
} from './SidebarAccordion.styled';

export const SidebarAccordion = (props: AccordionProps) => {
  const isSortable = Boolean(
    props.enableDnd &&
      props.onSortEnd &&
      !props.items.every((item) => item.tooltip),
  );

  return (
    <AccordionContainer>
      {isSortable ? (
        <SortableList
          lockAxis="y"
          onSortEnd={props.onSortEnd || (() => {})}
          draggedItemClassName="dragged">
          {props.items.map((item, index) => (
            <SortableItem key={`${item.columnId}-${index}`}>
              <SortableItemContainer>
                <Accordion
                  disableGutters
                  defaultExpanded={item.defaultExpanded}
                  onClick={item.onLabelClick}
                  {...(item.disabledExpand && { expanded: false })}>
                  <Summary
                    $enableDnd={props.isColumnList}
                    expandIcon={<KeyboardArrowDownIcon />}>
                    <SortableKnob>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                        }}>
                        {item.dragIcon}
                      </IconButton>
                    </SortableKnob>

                    <SummaryLabel $isError={item?.hasError}>
                      {item.label}
                    </SummaryLabel>

                    {item?.deleteIcon && (
                      <IconButton
                        sx={{ marginLeft: 'auto' }}
                        onClick={
                          (item.deleteIcon as ReactElement).props.onClick
                        }>
                        <DeleteOutlinedIcon color="error" />
                      </IconButton>
                    )}
                  </Summary>

                  <Box>{item.content}</Box>
                </Accordion>
              </SortableItemContainer>
            </SortableItem>
          ))}
        </SortableList>
      ) : (
        props.items.map((item, itemIndex) => (
          <CustomTooltip
            key={itemIndex}
            width={320}
            placement="right"
            skipValidateOverflow
            isHidden={!item?.disabled ? true : undefined}
            title={item.tooltip}>
            <StyledAccordian
              disableGutters
              disabled={item?.disabled}
              defaultExpanded={item.defaultExpanded}
              onClick={item.onLabelClick}
              {...(item.disabledExpand && { expanded: false })}>
              <Summary
                expandIcon={
                  item.chevronIcon ? (
                    item.chevronIcon
                  ) : (
                    <KeyboardArrowDownIcon />
                  )
                }>
                {item?.startIcon}

                {item.label ? (
                  <StepContainer>
                    {item.prefixNumber && (
                      <NumberContainer>{item.prefixNumber}</NumberContainer>
                    )}
                    <LabelsContainer>
                      <SummaryLabel $isError={item?.hasError}>
                        {typeof item.label === 'string'
                          ? getPlainText(item.label)
                          : item.label}
                        {item.hasCheckIcon && (
                          <CheckIcon
                            style={{ marginLeft: '5px' }}
                            color="success"
                          />
                        )}
                      </SummaryLabel>
                      {item.labelBadges?.map((badge, badgeIndex) => (
                        <Chip
                          key={`${itemIndex}-${badgeIndex}`}
                          label={badge}
                        />
                      ))}
                    </LabelsContainer>
                  </StepContainer>
                ) : (
                  item.titleComponent
                )}
              </Summary>

              <Box>{item.content}</Box>
            </StyledAccordian>
          </CustomTooltip>
        ))
      )}
    </AccordionContainer>
  );
};
